import React from 'react';
import { BOOKING_URL } from '../../../constants/config';
import {
  Container,
  Typography,
  Grid,
  Box,
  Paper,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  useTheme,
  useMediaQuery,
  Button,
} from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet';

const DentalBridges = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const benefits = [
    'Restores chewing function',
    'Maintains facial structure',
    'Prevents teeth shifting',
    'Natural appearance',
    'Fixed solution',
    'Long-lasting results'
  ];

  const process = [
    {
      title: 'Initial Consultation',
      description: 'We\'ll evaluate your oral health and determine if a bridge is the right solution.'
    },
    {
      title: 'Preparation',
      description: 'The adjacent teeth are prepared to support the bridge.'
    },
    {
      title: 'Impressions',
      description: 'We take precise impressions to create your custom bridge.'
    },
    {
      title: 'Temporary Bridge',
      description: 'A temporary bridge is placed while your permanent one is being made.'
    },
    {
      title: 'Final Placement',
      description: 'Your custom bridge is fitted and permanently cemented in place.'
    }
  ];

  return (
    <>
      <Helmet>
        <title>Dental Bridges | Pacifica Dental</title>
        <meta name="description" content="Replace missing teeth with custom dental bridges. Our bridges restore your smile and maintain proper dental function." />
        <meta name="keywords" content="dental bridges, tooth bridges, fixed bridges, missing teeth replacement, dental restoration" />
      </Helmet>
      
      <Box 
        sx={{ 
          py: { xs: 6, md: 8 },
          background: 'linear-gradient(135deg, #f5f5f5 0%, #ffffff 100%)',
        }}
      >
        <Container maxWidth="lg">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <Typography
              variant={isMobile ? "h4" : "h2"}
              component="h1"
              gutterBottom
              sx={{ 
                mb: 4,
                fontWeight: 700,
                color: 'text.primary'
              }}
            >
              Dental Bridges
            </Typography>

            <Typography
              variant="h5"
              component="h2"
              gutterBottom
              sx={{ mb: 4, color: 'text.secondary' }}
            >
              Replace Missing Teeth with Custom Bridges
            </Typography>

            <Typography
              variant="body1"
              paragraph
              sx={{ mb: 4, fontSize: '1.1rem', lineHeight: 1.7 }}
            >
              Dental bridges are a fixed solution for replacing one or more missing teeth. They consist of artificial teeth anchored to adjacent natural teeth or implants, providing a natural-looking and functional replacement that restores your smile and maintains proper dental function.
            </Typography>

            <Grid container spacing={4}>
              <Grid item xs={12} md={6}>
                <Paper
                  elevation={2}
                  sx={{
                    p: 3,
                    height: '100%',
                    backgroundColor: 'primary.light',
                    color: 'white',
                  }}
                >
                  <Typography variant="h6" gutterBottom>
                    Benefits of Dental Bridges
                  </Typography>
                  <List>
                    {benefits.map((benefit, index) => (
                      <ListItem key={index}>
                        <ListItemIcon>
                          <CheckCircleOutlineIcon sx={{ color: 'white' }} />
                        </ListItemIcon>
                        <ListItemText primary={benefit} />
                      </ListItem>
                    ))}
                  </List>
                </Paper>
              </Grid>

              <Grid item xs={12} md={6}>
                <Paper
                  elevation={2}
                  sx={{
                    p: 3,
                    height: '100%',
                  }}
                >
                  <Typography variant="h6" gutterBottom color="primary">
                    The Process
                  </Typography>
                  {process.map((step, index) => (
                    <Box key={index} sx={{ mb: 2 }}>
                      <Typography variant="subtitle1" color="primary" fontWeight="bold">
                        {step.title}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {step.description}
                      </Typography>
                    </Box>
                  ))}
                </Paper>
              </Grid>
            </Grid>

            <Box sx={{ mt: 6, textAlign: 'center' }}>
              <Button
                onClick={() => window.location.href = BOOKING_URL}
                variant="contained"
                color="secondary"
                size="large"
                sx={{
                  px: 5, 
                  py: 1.5,
                  borderRadius: 2,
                  textTransform: 'none',
                  fontWeight: 600,
                  fontSize: '1.1rem',
                  '&:hover': {
                    backgroundColor: 'secondary.dark',
                  }
                }}
              >
                Schedule Your Appointment
              </Button>
            </Box>

            <Box sx={{ mt: 6 }}>
              <Typography variant="h5" gutterBottom color="primary">
                Why Choose Our Bridge Services?
              </Typography>
              <List>
                <ListItem>
                  <ListItemIcon>
                    <CheckCircleOutlineIcon color="primary" />
                  </ListItemIcon>
                  <ListItemText 
                    primary="Custom Design"
                    secondary="Bridges tailored to your unique smile"
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    <CheckCircleOutlineIcon color="primary" />
                  </ListItemIcon>
                  <ListItemText 
                    primary="Quality Materials"
                    secondary="Durable materials for long-lasting results"
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    <CheckCircleOutlineIcon color="primary" />
                  </ListItemIcon>
                  <ListItemText 
                    primary="Natural Look"
                    secondary="Bridges that blend with your natural teeth"
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    <CheckCircleOutlineIcon color="primary" />
                  </ListItemIcon>
                  <ListItemText 
                    primary="Comprehensive Care"
                    secondary="Full support throughout your treatment"
                  />
                </ListItem>
              </List>
            </Box>
          </motion.div>
        </Container>
      </Box>
    </>
  );
};

export default DentalBridges; 